import React, { Suspense } from 'react';
import { getFileTypeV2 } from './fileTypeUtils';

// Lazy-load all viewers — only the active one is ever fetched and rendered
const ImageViewer = React.lazy(() => import('./viewers/ImageViewer'));
const VideoViewer = React.lazy(() => import('./viewers/VideoViewer'));
const PDFViewer = React.lazy(() => import('./viewers/PDFViewer'));
const OfficeViewer = React.lazy(() => import('./viewers/OfficeViewer'));
const TextViewer = React.lazy(() => import('./viewers/TextViewer'));
const CsvViewer = React.lazy(() => import('./viewers/CsvViewer'));
const UnsupportedViewer = React.lazy(() => import('./viewers/UnsupportedViewer'));

export const ViewerFallback = () => (
  <div className="viewer-loading">
    <div className="spinner-border text-primary" role="status" aria-label="Loading viewer">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

/**
 * Routes a file item to the correct viewer component.
 * Only one viewer is rendered at a time (the active slide).
 *
 * @param {{ item: object, isClientFileManager: boolean, onLoad: function }} props
 */
const ViewerRouter = ({ item, isClientFileManager, onLoad }) => {
  if (!item?.url) return <ViewerFallback />;

  const fileType = getFileTypeV2(item.display_name || item.file_name);

  const commonProps = { item, isClientFileManager, onLoad };

  let viewer;

  switch (fileType) {
    case 'image':
      viewer = <ImageViewer imageUrl={item.url} title={item.display_name} {...commonProps} />;
      break;
    case 'video':
      viewer = <VideoViewer {...commonProps} />;
      break;
    case 'pdf':
      viewer = <PDFViewer document={item.url} {...commonProps} />;
      break;
    case 'doc':
    case 'xls':
    case 'ppt':
      viewer = <OfficeViewer document={item.url} {...commonProps} />;
      break;
    case 'csv':
      viewer = <CsvViewer fileUrl={item.url} {...commonProps} />;
      break;
    case 'text':
      viewer = <TextViewer fileUrl={item.url} {...commonProps} />;
      break;
    default:
      viewer = <UnsupportedViewer {...commonProps} />;
  }

  return <Suspense fallback={<ViewerFallback />}>{viewer}</Suspense>;
};

export default React.memo(ViewerRouter);
